// @flow
import { push } from 'connected-react-router'
import { checkHttpStatus, getApiBaseUrl, getApiHeaders } from '../utils/api'
import { fetchDataAndRedirectUser } from './combined'
import { setCurrentOrganization, setMaxActiveQuests } from './organization'
import { setPoliciesToAccept, userNeedsToAcceptPolicies } from './policies'
import type { APIError } from '../types'
import { setGuardianUser } from './guardian'
import {
  removeLockedOutOrganizations,
  doesUserHaveAppAccess
} from 'utils/actions'
import { getUnsavedWorkMessage, willLoseUnsavedWork } from '../utils/unsavedwork'
import { clearUnsavedWork } from './unsavedwork'
import { postToLoggingServiceSimple } from './logging'
import { hideModal } from './_modal'

export const CLEAR_STATUS_TEXT = 'CLEAR_STATUS_TEXT'
export const LOGIN_USER_REQUEST = 'LOGIN_USER_REQUEST'
export const LOGIN_USER_SUCCESS = 'LOGIN_USER_SUCCESS'
export const UPDATE_MENTOR_STATUS = 'UPDATE_MENTOR_STATUS'
export const LOGIN_USER_FAILURE = 'LOGIN_USER_FAILURE'
export const LOGOUT_USER = 'LOGOUT_USER'
export const FORGOT_PASSWORD_REQUEST = 'FORGOT_PASSWORD_REQUEST'
export const FORGOT_PASSWORD_SUCCESS = 'FORGOT_PASSWORD_SUCCESS'
export const FORGOT_PASSWORD_FAILURE = 'FORGOT_PASSWORD_FAILURE'
export const RESET_PASSWORD_REQUEST = 'RESET_PASSWORD_REQUEST'
export const RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS'
export const RESET_PASSWORD_FAILURE = 'RESET_PASSWORD_FAILURE'
export const UNLOCK_ACCOUNT_REQUEST = 'UNLOCK_ACCOUNT_REQUEST'
export const UNLOCK_ACCOUNT_SUCCESS = 'UNLOCK_ACCOUNT_SUCCESS'
export const UNLOCK_ACCOUNT_FAILURE = 'UNLOCK_ACCOUNT_FAILURE'
export const USER_IS_LOGGED_IN_FETCHING_ALL_DATA =
  'USER_IS_LOGGED_IN_FETCHING_ALL_DATA'
export const USER_IS_LOGGED_IN_DATA_FETCHED = 'USER_IS_LOGGED_IN_DATA_FETCHED'
export const USER_IS_LOGGED_IN_REDIRECTED = 'USER_IS_LOGGED_IN_REDIRECTED'
export const CLEAR_RESET_PASSWORD = 'CLEAR_RESET_PASSWORD'
export const USER_NEEDS_TO_RESET_PASSWORD = 'USER_NEEDS_TO_RESET_PASSWORD'
export const SET_THEME = 'SET_THEME'

export function userNeedsToResetPassword(reset: boolean): Object {
  return {
    type: USER_NEEDS_TO_RESET_PASSWORD,
    userNeedsToResetPassword: reset
  }
}

export function clearStatusText(): Object {
  return {
    type: CLEAR_STATUS_TEXT
  }
}

export function userIsLoggedInFetchingAllData(): Object {
  return {
    type: USER_IS_LOGGED_IN_FETCHING_ALL_DATA
  }
}

export function userIsLoggedInDataFetched(): Object {
  return {
    type: USER_IS_LOGGED_IN_DATA_FETCHED
  }
}

export function userIsLoggedInRedirected(): Object {
  return {
    type: USER_IS_LOGGED_IN_REDIRECTED
  }
}

export function loginUserSuccess(response: Object, isMentor: boolean): Object {
  // requirement to store last login value
  localStorage.setItem('lastLogin', response.email)
  sessionStorage.setItem('oef-token', response.token)
  return {
    type: LOGIN_USER_SUCCESS,
    user: response,
    isMentor: isMentor
  }
}

export function loginUserFailure(error: APIError): Object {
  return {
    type: LOGIN_USER_FAILURE,
    payload: {
      status: error.response.status,
      statusText: error.response.statusText
    }
  }
}

export function loginUserRequest(): Object {
  return {
    type: LOGIN_USER_REQUEST
  }
}

export function logout(): Object {
  sessionStorage.clear()
  return {
    type: LOGOUT_USER
  }
}

export function forgotPasswordRequest(): Object {
  return {
    type: FORGOT_PASSWORD_REQUEST
  }
}

export function forgotPasswordSuccess(): Object {
  return {
    type: FORGOT_PASSWORD_SUCCESS
  }
}

export function forgotPasswordFailure(): Object {
  alert('There was an error submitting your forgot password request.')
  return {
    type: FORGOT_PASSWORD_FAILURE
  }
}

export function clearResetPassword(): Object {
  return {
    type: CLEAR_RESET_PASSWORD
  }
}

export function resetPasswordRequest(): Object {
  return {
    type: RESET_PASSWORD_REQUEST
  }
}

export function resetPasswordSuccess(): Object {
  return {
    type: RESET_PASSWORD_SUCCESS
  }
}

export function resetPasswordFailure(): Object {
  return {
    type: RESET_PASSWORD_FAILURE
  }
}

export function unlockAccountRequest(): Object {
  return {
    type: UNLOCK_ACCOUNT_REQUEST
  }
}

export function unlockAccountSuccess(): Object {
  return {
    type: UNLOCK_ACCOUNT_SUCCESS
  }
}

export function unlockAccountFailure(error: APIError): Object {
  return {
    type: UNLOCK_ACCOUNT_FAILURE,
    payload: {
      unlockErrorStatusText: error.response.statusText
    }
  }
}

export function updateMentorStatus(mentorStatus: boolean): Object {
  return {
    type: UPDATE_MENTOR_STATUS,
    payload: mentorStatus
  }
}

export function setThemeOption(theme: string): Object {
  return {
    type: SET_THEME,
    payload: theme
  }
}


export function forgotPassword(email: string): Function {
  return async (dispatch, getState) => {
    const state = getState()
    dispatch(forgotPasswordRequest())

    try {
      checkHttpStatus(
        await fetch(`${getApiBaseUrl()}/api/password_resets`, {
          method: 'POST',
          headers: getApiHeaders(state),
          body: JSON.stringify({
            email: email
          })
        })
      )

      dispatch(forgotPasswordSuccess())
    } catch (error) {
      dispatch(forgotPasswordFailure())
    }
  }
}

export function resetPassword(token: string, password: string): Function {
  return async (dispatch, getState) => {
    const state = getState()
    dispatch(resetPasswordRequest())

    try {
      checkHttpStatus(
        await fetch(`${getApiBaseUrl()}/api/password_resets/${token}`, {
          method: 'PATCH',
          headers: getApiHeaders(state),
          body: JSON.stringify({
            user: {
              password: password,
              password_confirmation: password
            }
          })
        })
      )

      dispatch(resetPasswordSuccess())
    } catch (error) {
      dispatch(resetPasswordFailure())
    }
  }
}

export function unlockAccount(token: string): Function {
  return async (dispatch, getState) => {
    const state = getState()
    dispatch(unlockAccountRequest())

    try {
      checkHttpStatus(
        await fetch(`${getApiBaseUrl()}/api/unlock_accounts/${token}`, {
          method: 'PUT',
          headers: getApiHeaders(state)
        })
      )

      dispatch(unlockAccountSuccess())
    } catch (error) {
      if (error.message === 'Unauthorized') {
        error.message = 'This page is no longer available.'
      }
      dispatch(unlockAccountFailure(getErrorMessageObj(error.message)))
    }
  }
}

function handleLoginSuccess(user: Object): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const theme = state.auth.theme

    if (user.email.trim() !== localStorage.getItem('lastLogin')) {
      localStorage.removeItem('courseId')
    }

    let currentOrg = user.org_id
      ? user.organizations.find(org => org.id === user.org_id)
      : user.organizations[0]

    // determine if user is a mentor or guardian
    const isMentor = currentOrg.roles.includes("qa_mentor")
    const isGuardian = user.guardian_user ? user.guardian_user : false

    dispatch(setMaxActiveQuests(currentOrg ? currentOrg.max_active_quests : 6))

    const policiesToAccept = await userNeedsToAcceptPolicies(user.token)

    await dispatch(loginUserSuccess(user, isMentor))
    dispatch(setCurrentOrganization(currentOrg.id, isMentor, false))

    dispatch(postToLoggingServiceSimple('qw_user_login', {
      action: 'user_login',
      user: { id: user.id, name: user.name, role: isMentor ? 'mentor' : isGuardian ? 'guardian' : 'student' },
      theme: { theme },
      organization: { id: currentOrg.id, name: currentOrg.name },
    }))

    if (policiesToAccept && isGuardian) {
      await dispatch(push('/policies?il=1'))
      await dispatch(setGuardianUser(user))
      dispatch(setPoliciesToAccept(policiesToAccept))

    } else if (policiesToAccept) {
      await dispatch(push('/policies?il=1'))
      dispatch(setPoliciesToAccept(policiesToAccept, user))

    } else if(isGuardian) {
      await dispatch(setGuardianUser(user))
      dispatch(push('/guardianlobby'))

    } else {
      await dispatch(userIsLoggedInFetchingAllData())
      dispatch(fetchDataAndRedirectUser(user, currentOrg, isMentor, isGuardian))
    }
  }
}

function getErrorMessageObj(msg: string) {
  return {
    response: {
      status: 401,
      statusText: msg
    }
  }
}

export function loginUser(email: string, password: string): Function {
  return async dispatch => {
    const data = new window.FormData()
    data.append('session[email]', email.trim())
    data.append('session[password]', password.trim())
    data.append('session[application]', 'QW')

    dispatch(loginUserRequest())

    try {
      const response = checkHttpStatus(
        await fetch(`${getApiBaseUrl()}/api/login`, {
          method: 'POST',
          body: data
        })
      )

      let user = await response.json()

      user = removeLockedOutOrganizations(user)

      if (doesUserHaveAppAccess(user)) {
        let lastSelectedOrgId = localStorage.getItem('lastSelectedOrgId')
        let lastSelectedUserId = localStorage.getItem('lastSelectedUserId')
        lastSelectedUserId = lastSelectedUserId
          ? parseInt(lastSelectedUserId)
          : null

        if (
          lastSelectedOrgId &&
          lastSelectedUserId === user.id &&
          // if user org roles change, lastSelectedOrgId may no longer exist in the filtered app orgs list
          user.organizations.some(o => o.id.toString() === lastSelectedOrgId)
        ) {
          user.org_id = parseInt(lastSelectedOrgId)
        }

        localStorage.setItem('lastSelectedUserId', user.id)

        dispatch(handleLoginSuccess(user))
      } else {
        throw new Error(
          'Quest! Web is restricted. Kindly use the Quest! Tablet app.'
        )
      }
    } catch (error) {
      // replace the server response when unauthorized
      if (error.message === 'Unauthorized') {
        error.message = 'Oops! Your username or password was incorrect.'
      }
      dispatch(loginUserFailure(getErrorMessageObj(error.message)))
    }
  }
}

export function logoutAndRedirect(
  reload: boolean,
  redirect: boolean
): Function {
  return async (dispatch, getState) => {
    const unsavedWork = getState().unsavedwork.items
    if (
      !willLoseUnsavedWork({ unsavedWork }) ||
      window.confirm(getUnsavedWorkMessage())
    ) {
      dispatch(clearUnsavedWork())
      dispatch(logout())
      if (reload) {
        window.location.reload(true)
      } else {
        dispatch(
          push(
            redirect ? `/login?redirect=${window.location.pathname}` : '/login'
          )
        )
      }
    }
  }
}

export function redirectAuthenticatedUser(): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const user = state.auth.user
    dispatch(
      push(
        user.guardian_user
          ? '/guardianlobby'
          : !state.auth.isMentor
          ? `/user/${user.id}/dashboard`
          : state.auth.userHasCurrentCourses
          ? '/mystudents/journeyoverview'
          : state.auth.userHasPastCourses
          ? '/mentordashboard'
          : '/mycommunity'
      )
    )
  }
}
